import {useState, useEffect, forwardRef} from 'react';

import {styled, TextField, TextFieldProps} from '@mui/material';

export type InputProps = Omit<TextFieldProps, 'defaultProps' | 'value'> & {value?: string | number};

export const InputStyled = styled(TextField)(({theme}) => ({
  '& label.MuiFormLabel-root, & label.MuiFormLabel-colorPrimary': {
    color: theme.palette.grey[400],
    width: '95%',
    transform: `translate(${theme.spacing(1.5)}, 18px) scale(1)`,
    '&.MuiInputLabel-sizeSmall': {
      transform: `translate(${theme.spacing(1.5)}, 14px) scale(1)`
    },
    '&.Mui-focused, &.custom-input-has-value': {
      transform: `translate(${theme.spacing(1.5)}, 7px) scale(0.75)`
    },
    '&.Mui-disabled': {
      color: theme.palette.grey[300]
    },
    '&.Mui-error': {
      color: theme.palette.error.main
    }
  },
  '& .MuiFilledInput-root': {
    border: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
    borderRadius: 4,
    backgroundColor: `${theme.palette.common.white} !important`,
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:before, &:after': {
      display: 'none !important'
    },
    '&:hover': {
      backgroundColor: theme.palette.common.white
    },
    '&.Mui-focused, &:hover': {
      borderColor: theme.palette.secondary.main
    },
    '& input': {
      '&::placeholder': {
        color: 'transparent'
      }
    },
    '&.Mui-focused': {
      outline: `${theme.palette.secondary.main} solid 1px`,
      '& input': {
        '&::placeholder': {
          color: 'inherit'
        }
      },
      '&.Mui-error': {
        outline: `${theme.palette.error.main} solid 1px`
      }
    },
    '&.Mui-disabled': {
      backgroundColor: `${theme.palette.grey[50]} !important`,
      '& input.Mui-disabled': {
        textFillColor: theme.palette.grey[400]
      }
    },
    '&.Mui-error': {
      borderColor: theme.palette.error.main
    }
  }
})) as typeof TextField;

export const Input = forwardRef(({onKeyUp, ...props}: InputProps, ref): JSX.Element => {
  const [hasValue, setHasValue] = useState(Boolean(props?.defaultValue || props?.value));
  const inputLabelProps = props.InputLabelProps || {};

  useEffect(() => {
    if (hasValue !== (props.defaultValue || props.value)) {
      setHasValue(Boolean(props.defaultValue || props.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.defaultValue]);

  const handleKeyUpChange = (evt: any): void => {
    if (onKeyUp) {
      onKeyUp(evt);
    }
  };

  return (
    <InputStyled
      onKeyUp={handleKeyUpChange}
      variant="filled"
      size="medium"
      // Incoming props should be applied on top
      {...props}
      inputRef={ref}
      // Custom handling restricted use case to update the props object
      helperText={props.error ? props?.helperText : ''}
      InputLabelProps={{shrink: true, className: hasValue ? 'custom-input-has-value' : '', ...inputLabelProps}}
    />
  );
});

Input.displayName = 'Input';

export default Input;
